import { onLine, partLineCount, type PartLine, type Timeline, type VoicePart } from '@corocanto/scoring';
import { noteColorKey } from './noteKey';

/** Helpers for a divided part (divisi): the chorist sings one line, 1 (upper) or 2 (lower). */

/** True if the part divides somewhere in this song. */
export function partDivides(timeline: Timeline, part: VoicePart | null): boolean {
  return part !== null && partLineCount(timeline, part) === 2;
}

/**
 * The staves to emphasize for a part and line: the part's staves that have notes on that line.
 * Where Soprano 1 and 2 have their own staves, that is only the chorist's staff.
 */
export function lineStaffIds(timeline: Timeline, part: VoicePart, line: PartLine | null): string[] {
  const all = [...new Set(timeline.notes.filter((n) => n.voicePart === part).map((n) => n.partId))];
  if (line === null) return all;
  const mine = all.filter((id) => timeline.notes.some((n) => n.partId === id && n.voicePart === part && onLine(n, line)));
  return mine.length > 0 ? mine : all;
}

/** The notes of the other line, keyed like note colors, to draw gray on the chorist's staff. */
export function otherLineNoteKeys(timeline: Timeline, part: VoicePart, line: PartLine): string[] {
  const keys = new Set<string>();
  for (const n of timeline.notes) {
    if (n.voicePart === part && !onLine(n, line)) keys.add(noteColorKey(n.partId, n.measureIndex, n.beatOffset, n.spelling));
  }
  // A note both lines sing (the same pitch written twice) stays black.
  for (const n of timeline.notes) {
    if (n.voicePart === part && onLine(n, line)) keys.delete(noteColorKey(n.partId, n.measureIndex, n.beatOffset, n.spelling));
  }
  return [...keys];
}

/** The written bars where a part divides, as their printed numbers in score order. */
export function dividedBars(timeline: Timeline, part: VoicePart, measureNumbers: readonly string[]): string[] {
  const indexes = new Set(timeline.notes.filter((n) => n.voicePart === part && n.line !== 0).map((n) => n.measureIndex));
  return [...indexes].sort((a, b) => a - b).map((i) => measureNumbers[i] ?? String(i + 1));
}

/** Bar numbers as a short list with ranges: ["2", "3", "4", "7"] → "2–4, 7" (consecutive in the list given). */
export function barList(numbers: readonly string[], allNumbers: readonly string[]): string {
  const index = (n: string) => allNumbers.indexOf(n);
  const groups: string[][] = [];
  for (const n of numbers) {
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    if (last && prev !== undefined && index(n) === index(prev) + 1) last.push(n);
    else groups.push([n]);
  }
  return groups.map((g) => (g.length === 1 ? g[0] : `${g[0]}–${g[g.length - 1]}`)).join(', ');
}
